import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { apiClient, API } from "../App";
import PlayerPicker from "../components/PlayerPicker";
import SortableTableHead from "../components/SortableTableHead";
import TableSearchInput from "../components/TableSearchInput";
import useTableControls from "../hooks/useTableControls";
import useScrollRestoration from "../hooks/useScrollRestoration";
import { ArrowLeft, Package, Plus, Pencil, Trash2, Download, UserPlus, Undo2, History, Image as ImageIcon, Upload, X as XIcon, ShieldCheck } from "lucide-react";

const CATEGORIES = ["uniform", "instrument", "accessory"];
const CONDITIONS = ["excellent", "good", "fair", "poor", "damaged", "stolen", "sold", "unknown_location"];
// iter-34: stolen + sold items are excluded from the band's total inventory
// value. Surfaced visually as a strike-through Badge so admins can spot them.
const VALUE_EXCLUDED_CONDITIONS = new Set(["stolen", "sold"]);
const formatCondition = (c) => (c || "good").replace(/_/g, " ");
const fmt = (n) => n != null ? `£${Number(n).toFixed(2)}` : "—";

// Iter-69: column configs for sort + per-column filter dropdowns.
// `accessor` returns the value used for both sort AND filter dropdown bucketing.
// `filterFormatter` (optional) controls how the dropdown groups values.
const INVENTORY_COLUMNS = {
  instrument: [
    { key: "photo", label: "Photo", accessor: () => null, sortable: false, filterable: false, searchable: false },
    { key: "asset_tag", label: "Asset Tag", accessor: "asset_tag", sortable: true, filterable: false },
    { key: "subcategory", label: "Type", accessor: (r) => r.subcategory || r.name, sortable: true, filterable: true },
    { key: "make", label: "Make / Finish", accessor: (r) => `${r.make || ""} ${r.finish || ""}`.trim(), sortable: true, filterable: false },
    { key: "serial_number", label: "Serial", accessor: "serial_number", sortable: true, filterable: false },
    { key: "purchase_price", label: "Value", accessor: (r) => r.purchase_price ?? null, sortable: true, filterable: false },
    { key: "condition", label: "Condition", accessor: "condition", sortable: true, filterable: true, filterFormatter: (v) => formatCondition(v) },
    { key: "assigned", label: "Assigned To", accessor: (r) => r.assigned_to_player_name || null, sortable: true, filterable: true, filterFormatter: (v) => v || "Store Room" },
  ],
  uniform: [
    { key: "photo", label: "Photo", accessor: () => null, sortable: false, filterable: false, searchable: false },
    { key: "subcategory", label: "Type", accessor: "subcategory", sortable: true, filterable: true },
    { key: "name", label: "Item", accessor: "name", sortable: true, filterable: false },
    { key: "size", label: "Size", accessor: "size", sortable: true, filterable: true },
    { key: "condition", label: "Condition", accessor: "condition", sortable: true, filterable: true, filterFormatter: (v) => formatCondition(v) },
    { key: "supplier", label: "Vendor", accessor: "supplier", sortable: true, filterable: true },
    { key: "purchase_price", label: "Value", accessor: (r) => r.purchase_price ?? null, sortable: true, filterable: false },
    { key: "assigned", label: "Assigned To", accessor: (r) => r.assigned_to_player_name || null, sortable: true, filterable: true, filterFormatter: (v) => v || "Store Room" },
  ],
  accessory: [
    { key: "photo", label: "Photo", accessor: () => null, sortable: false, filterable: false, searchable: false },
    { key: "asset_tag", label: "Asset Tag", accessor: "asset_tag", sortable: true, filterable: false },
    { key: "make", label: "Make", accessor: "make", sortable: true, filterable: true },
    { key: "name", label: "Item Name", accessor: "name", sortable: true, filterable: false },
    { key: "purchase_date", label: "Purchase Date", accessor: (r) => r.purchase_date ? new Date(r.purchase_date) : null, sortable: true, filterable: false },
    { key: "purchase_price", label: "Value", accessor: (r) => r.purchase_price ?? null, sortable: true, filterable: false },
    { key: "assigned", label: "Assigned To", accessor: (r) => r.assigned_to_player_name || null, sortable: true, filterable: true, filterFormatter: (v) => v || "Store Room" },
  ],
};

const Inventory = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  // iter-86: preserve scroll position on detail navigation
  useScrollRestoration({ ready: items.length > 0 });
  const [players, setPlayers] = useState([]);
  const [sections, setSections] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterCat, setFilterCat] = useState("instrument");
  const [filterAssigned, setFilterAssigned] = useState("all");
  const [search, setSearch] = useState("");

  const [dialog, setDialog] = useState({ open: false, data: null });
  const [issueDialog, setIssueDialog] = useState({ open: false, item: null, player_id: "", return_due_date: "", notes: "" });
  const [returnDialog, setReturnDialog] = useState({ open: false, item: null, returned_condition: "", notes: "" });
  const [historyDialog, setHistoryDialog] = useState({ open: false, item: null, loans: [] });
  const [photoPreview, setPhotoPreview] = useState(null); // {id, name}
  const [photoUploading, setPhotoUploading] = useState(false);
  const [uniformOptions, setUniformOptions] = useState([]);

  const loadAll = async () => {
    try {
      const params = {};
      if (filterCat !== "all") params.category = filterCat;
      if (filterAssigned === "assigned") params.assigned = true;
      if (filterAssigned === "storeroom") params.assigned = false;
      if (search) params.search = search;
      const [it, st, pl, uo, sx] = await Promise.all([
        apiClient.get("/inventory", { params }),
        apiClient.get("/inventory/stats"),
        apiClient.get("/players"),
        apiClient.get("/band/uniform-options").catch(() => ({ data: { options: [] } })),
        apiClient.get("/sections").catch(() => ({ data: [] })),
      ]);
      setItems(it.data);
      setStats(st.data);
      setPlayers(pl.data || []);
      setUniformOptions(uo.data?.options || []);
      setSections(sx.data || []);
    } catch (e) {
      toast.error("Failed to load inventory");
    }
  };

  useEffect(() => { loadAll(); }, [filterCat, filterAssigned]); // eslint-disable-line

  // Iter-69→70: sort + per-column filter state. Columns config switches with
  // the active tab. Default sort: Asset Tag ascending (iter-70 user request).
  const activeColumns = INVENTORY_COLUMNS[filterCat] || INVENTORY_COLUMNS.instrument;
  const defaultSortKey =
    filterCat === "uniform" ? "name" : "asset_tag"; // uniforms have no asset_tag column
  const tableCtl = useTableControls(items, activeColumns, { defaultSortKey, defaultSortDir: "asc" });
  const visibleRows = tableCtl.processedRows;

  const handleSave = async () => {
    const d = dialog.data;
    if (!d?.category) { toast.error("Category is required"); return; }
    // Item label only exists on Accessory now. For Instrument & Uniform we
    // derive a name from subcategory (+ asset_tag for uniqueness if available)
    // so the backend `name` field is always populated.
    let name = (d.name || "").trim();
    if (!name) {
      if (d.category === "accessory") {
        toast.error("Item Name is required for accessories");
        return;
      }
      const sub = (d.subcategory || "").trim();
      if (!sub) {
        toast.error(d.category === "instrument" ? "Instrument Type is required" : "Uniform Type is required");
        return;
      }
      name = d.asset_tag ? `${sub} (${d.asset_tag})` : sub;
    }
    const payload = {
      category: d.category,
      subcategory: d.subcategory || null,
      name,
      asset_tag: d.asset_tag || null,
      make: d.make || null,
      finish: d.finish || null,
      serial_number: d.serial_number || null,
      size: d.size || null,
      condition: d.condition || "good",
      purchase_date: d.purchase_date || null,
      purchase_price: d.purchase_price ? parseFloat(d.purchase_price) : null,
      supplier: d.supplier || null,
      notes: d.notes || null,
    };
    try {
      if (d.id) await apiClient.put(`/inventory/${d.id}`, payload);
      else await apiClient.post("/inventory", payload);
      toast.success(d.id ? "Updated" : "Added");
      setDialog({ open: false, data: null });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item? Loan history will also be removed.")) return;
    try {
      await apiClient.delete(`/inventory/${id}`);
      toast.success("Deleted");
      loadAll();
    } catch (e) { toast.error("Failed"); }
  };

  const handleIssue = async () => {
    try {
      await apiClient.post(`/inventory/${issueDialog.item.id}/issue`, {
        player_id: issueDialog.player_id,
        return_due_date: issueDialog.return_due_date || null,
        notes: issueDialog.notes || null,
      });
      toast.success("Issued");
      setIssueDialog({ open: false, item: null, player_id: "", return_due_date: "", notes: "" });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleReturn = async () => {
    try {
      await apiClient.post(`/inventory/${returnDialog.item.id}/return`, {
        returned_condition: returnDialog.returned_condition || null,
        notes: returnDialog.notes || null,
      });
      toast.success("Returned to store room");
      setReturnDialog({ open: false, item: null, returned_condition: "", notes: "" });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const showHistory = async (item) => {
    try {
      const r = await apiClient.get(`/inventory/${item.id}`);
      setHistoryDialog({ open: true, item, loans: r.data.loans || [] });
    } catch (e) { toast.error("Failed to load history"); }
  };

  const exportFile = async (url, filename) => {
    try {
      const res = await apiClient.get(url, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.error("Export failed"); }
  };

  // Cell renderers shared across the three category-aware tables
  const renderPhotoCell = (it) => (
    it.photo_path ? (
      <button
        type="button"
        onClick={() => setPhotoPreview({ id: it.id, name: it.name })}
        className="w-10 h-10 rounded-md overflow-hidden border border-white/10 hover:border-primary/50 transition cursor-pointer"
        title="View photo"
        data-testid={`inv-photo-thumb-${it.id}`}
      >
        {/* iter-134b: cache-bust with the item's updated_at so a replaced
            photo shows up immediately after upload without a hard refresh.
            Without this, the browser served the previous file from cache
            because the URL was static. */}
        <img
          src={`${API}/inventory/${it.id}/photo?v=${encodeURIComponent(it.updated_at || "")}`}
          alt=""
          className="w-full h-full object-cover"
        />
      </button>
    ) : (
      <div className="w-10 h-10 rounded-md border border-dashed border-white/10 flex items-center justify-center text-muted-foreground/60" title="No photo">
        <ImageIcon className="w-4 h-4" />
      </div>
    )
  );

  const renderActionsCell = (it) => (
    <div className="flex justify-end gap-1">
      {it.assigned_to_player_id ? (
        <Button size="icon" variant="ghost" title="Return" onClick={() => setReturnDialog({ open: true, item: it, returned_condition: it.condition, notes: "" })} data-testid={`inv-return-${it.id}`}>
          <Undo2 className="w-4 h-4" />
        </Button>
      ) : (
        <Button size="icon" variant="ghost" title="Issue" onClick={() => setIssueDialog({ open: true, item: it, player_id: "", return_due_date: "", notes: "" })} data-testid={`inv-issue-${it.id}`}>
          <UserPlus className="w-4 h-4" />
        </Button>
      )}
      <Button size="icon" variant="ghost" title="History" onClick={() => showHistory(it)}><History className="w-4 h-4" /></Button>
      <Button size="icon" variant="ghost" title="Edit" onClick={() => setDialog({ open: true, data: { ...it } })}><Pencil className="w-4 h-4" /></Button>
      <Button size="icon" variant="ghost" title="Delete" onClick={() => handleDelete(it.id)}><Trash2 className="w-4 h-4" /></Button>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="inventory-page">
      <Button variant="ghost" onClick={() => navigate("/admin")} data-testid="inventory-back">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin
      </Button>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <Package className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-4xl tracking-tight">Inventory</h1>
          <p className="text-muted-foreground mt-1">Uniforms, instruments, accessories — assigned to players or in the store room.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
        <Card className="glass-card"><CardContent className="p-3 sm:p-4">
          <div className="text-[11px] sm:text-xs text-muted-foreground uppercase tracking-wider">Total</div>
          <div className="text-xl sm:text-2xl font-heading mt-0.5 sm:mt-1" data-testid="stat-total">{stats?.total_items || 0}</div>
        </CardContent></Card>
        <Card className="glass-card"><CardContent className="p-3 sm:p-4">
          <div className="text-[11px] sm:text-xs text-muted-foreground uppercase tracking-wider">Assigned</div>
          <div className="text-xl sm:text-2xl font-heading mt-0.5 sm:mt-1" data-testid="stat-assigned">{stats?.assigned || 0}</div>
        </CardContent></Card>
        <Card className="glass-card"><CardContent className="p-3 sm:p-4">
          <div className="text-[11px] sm:text-xs text-muted-foreground uppercase tracking-wider">Store Room</div>
          <div className="text-xl sm:text-2xl font-heading mt-0.5 sm:mt-1" data-testid="stat-storeroom">{stats?.in_storeroom || 0}</div>
        </CardContent></Card>
        <Card className="glass-card"><CardContent className="p-3 sm:p-4">
          <div className="text-[11px] sm:text-xs text-muted-foreground uppercase tracking-wider">Total Value</div>
          <div className="text-xl sm:text-2xl font-heading mt-0.5 sm:mt-1" data-testid="stat-value">{fmt(stats?.total_value)}</div>
          {stats?.excluded_value > 0 && (
            <div className="text-[10px] sm:text-[11px] text-muted-foreground mt-1" data-testid="stat-excluded-value">
              {fmt(stats.excluded_value)} excluded (stolen / sold)
            </div>
          )}
        </CardContent></Card>
      </div>

      {/* Filters + actions */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Search</Label>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadAll()} placeholder="Name, serial..." data-testid="inv-search" />
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={filterAssigned} onValueChange={setFilterAssigned}>
            <SelectTrigger className="w-40" data-testid="inv-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="storeroom">Store Room</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={loadAll} data-testid="inv-apply">Apply</Button>
        <div className="ml-auto flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => exportFile("/reports/inventory.pdf", "inventory.pdf")} data-testid="inv-export-pdf">
            <Download className="w-4 h-4 mr-1.5" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportFile("/reports/inventory.xlsx", "inventory.xlsx")} data-testid="inv-export-excel">
            <Download className="w-4 h-4 mr-1.5" /> Excel
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
            onClick={() => exportFile("/reports/inventory/insurance.pdf", "insurance-schedule.pdf")}
            data-testid="inv-export-insurance"
            title="Insurance-ready schedule, excludes stolen / sold items"
          >
            <ShieldCheck className="w-4 h-4 mr-1.5" /> Insurance Report
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
            onClick={() => exportFile("/reports/inventory/uniforms.pdf", "uniform-inventory.pdf")}
            data-testid="inv-export-uniforms-pdf"
            title="Uniform inventory grouped by player — PDF"
          >
            <Download className="w-4 h-4 mr-1.5" /> Uniform PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
            onClick={() => exportFile("/reports/inventory/uniforms.xlsx", "uniform-inventory.xlsx")}
            data-testid="inv-export-uniforms-xlsx"
            title="Uniform inventory grouped by player — Excel"
          >
            <Download className="w-4 h-4 mr-1.5" /> Uniform Excel
          </Button>
          <Button onClick={() => setDialog({ open: true, data: { category: filterCat, condition: "good" } })} data-testid="inv-add-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Item
          </Button>
        </div>
      </div>

      {/* Tabs — one per category, with category-aware columns */}
      <Tabs
        value={filterCat === "all" ? "instrument" : filterCat}
        onValueChange={(v) => setFilterCat(v)}
        className="space-y-3"
      >
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="instrument" data-testid="inv-tab-instrument">
            Instruments {stats?.by_category?.instrument ? `(${stats.by_category.instrument})` : ""}
          </TabsTrigger>
          <TabsTrigger value="uniform" data-testid="inv-tab-uniform">
            Uniforms {stats?.by_category?.uniform ? `(${stats.by_category.uniform})` : ""}
          </TabsTrigger>
          <TabsTrigger value="accessory" data-testid="inv-tab-accessory">
            Accessories {stats?.by_category?.accessory ? `(${stats.by_category.accessory})` : ""}
          </TabsTrigger>
        </TabsList>

        {/* INSTRUMENTS */}
        <TabsContent value="instrument">
          <Card className="glass-card">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {activeColumns.map((col) => (
                      <SortableTableHead
                        key={col.key}
                        column={col}
                        sortKey={tableCtl.sortKey}
                        sortDir={tableCtl.sortDir}
                        onToggleSort={() => tableCtl.toggleSort(col.key)}
                        rows={items}
                        selectedFilters={tableCtl.filters[col.key]}
                        onChangeFilter={(set) => tableCtl.setColumnFilter(col.key, set)}
                        className={col.key === "photo" ? "w-14" : undefined}
                      />
                    ))}
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleRows.map((it) => (
                    <TableRow key={it.id} data-testid={`inv-row-${it.id}`}>
                      <TableCell className="p-1">{renderPhotoCell(it)}</TableCell>
                      <TableCell className="font-mono text-xs">{it.asset_tag || "—"}</TableCell>
                      <TableCell>
                        <div className="font-medium">{it.subcategory || it.name}</div>
                        {it.subcategory && it.name !== it.subcategory && (
                          <div className="text-xs text-muted-foreground">{it.name}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {it.make || "—"}
                        {it.finish && <div className="text-xs text-muted-foreground">{it.finish}</div>}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{it.serial_number || "—"}</TableCell>
                      <TableCell className={`font-mono text-xs ${VALUE_EXCLUDED_CONDITIONS.has(it.condition) ? "line-through text-muted-foreground" : ""}`}>
                        {fmt(it.purchase_price)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={VALUE_EXCLUDED_CONDITIONS.has(it.condition) ? "destructive" : "secondary"}
                          className="capitalize"
                        >
                          {formatCondition(it.condition)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {it.assigned_to_player_name ? (
                          <span className="text-primary text-sm">{it.assigned_to_player_name}</span>
                        ) : (
                          <span className="text-muted-foreground text-xs">Store Room</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{renderActionsCell(it)}</TableCell>
                    </TableRow>
                  ))}
                  {visibleRows.length === 0 && (
                    <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">{items.length === 0 ? "No instruments yet. Click \"Add Item\" above." : "No matches — adjust filters above."}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* UNIFORMS */}
        <TabsContent value="uniform">
          <Card className="glass-card">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {activeColumns.map((col) => (
                      <SortableTableHead
                        key={col.key}
                        column={col}
                        sortKey={tableCtl.sortKey}
                        sortDir={tableCtl.sortDir}
                        onToggleSort={() => tableCtl.toggleSort(col.key)}
                        rows={items}
                        selectedFilters={tableCtl.filters[col.key]}
                        onChangeFilter={(set) => tableCtl.setColumnFilter(col.key, set)}
                        className={col.key === "photo" ? "w-14" : undefined}
                      />
                    ))}
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleRows.map((it) => (
                    <TableRow key={it.id} data-testid={`inv-row-${it.id}`}>
                      <TableCell className="p-1">{renderPhotoCell(it)}</TableCell>
                      <TableCell>
                        {it.subcategory ? (
                          <Badge variant="outline">{it.subcategory}</Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{it.name}</TableCell>
                      <TableCell className="font-mono text-xs">{it.size || "—"}</TableCell>
                      <TableCell>
                        <Badge
                          variant={VALUE_EXCLUDED_CONDITIONS.has(it.condition) ? "destructive" : "secondary"}
                          className="capitalize"
                        >
                          {formatCondition(it.condition)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{it.supplier || "—"}</TableCell>
                      <TableCell className={`font-mono text-xs ${VALUE_EXCLUDED_CONDITIONS.has(it.condition) ? "line-through text-muted-foreground" : ""}`}>
                        {fmt(it.purchase_price)}
                      </TableCell>
                      <TableCell>
                        {it.assigned_to_player_name ? (
                          <span className="text-primary text-sm">{it.assigned_to_player_name}</span>
                        ) : (
                          <span className="text-muted-foreground text-xs">Store Room</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{renderActionsCell(it)}</TableCell>
                    </TableRow>
                  ))}
                  {visibleRows.length === 0 && (
                    <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">{items.length === 0 ? "No uniform items yet. Click \"Add Item\" above." : "No matches — adjust filters above."}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* ACCESSORIES */}
        <TabsContent value="accessory">
          <Card className="glass-card">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {activeColumns.map((col) => (
                      <SortableTableHead
                        key={col.key}
                        column={col}
                        sortKey={tableCtl.sortKey}
                        sortDir={tableCtl.sortDir}
                        onToggleSort={() => tableCtl.toggleSort(col.key)}
                        rows={items}
                        selectedFilters={tableCtl.filters[col.key]}
                        onChangeFilter={(set) => tableCtl.setColumnFilter(col.key, set)}
                        className={col.key === "photo" ? "w-14" : undefined}
                      />
                    ))}
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleRows.map((it) => (
                    <TableRow key={it.id} data-testid={`inv-row-${it.id}`}>
                      <TableCell className="p-1">{renderPhotoCell(it)}</TableCell>
                      <TableCell className="font-mono text-xs">{it.asset_tag || "—"}</TableCell>
                      <TableCell className="text-sm">{it.make || "—"}</TableCell>
                      <TableCell className="font-medium">{it.name}</TableCell>
                      <TableCell className="text-xs">{it.purchase_date ? it.purchase_date.slice(0, 10) : "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{fmt(it.purchase_price)}</TableCell>
                      <TableCell>
                        {it.assigned_to_player_name ? (
                          <span className="text-primary text-sm">{it.assigned_to_player_name}</span>
                        ) : (
                          <span className="text-muted-foreground text-xs">Store Room</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{renderActionsCell(it)}</TableCell>
                    </TableRow>
                  ))}
                  {visibleRows.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">{items.length === 0 ? "No accessories yet. Click \"Add Item\" above." : "No matches — adjust filters above."}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, data: null })}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{dialog.data?.id ? "Edit" : "Add"} Item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Category *</Label>
              <Select value={dialog.data?.category} onValueChange={(v) => setDialog({ open: true, data: { ...dialog.data, category: v } })}>
                <SelectTrigger data-testid="inv-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* ===== INSTRUMENT FORM ===== */}
            {dialog.data?.category === "instrument" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Asset Tag</Label>
                    <Input
                      value={dialog.data?.asset_tag || ""}
                      onChange={(e) => setDialog({ open: true, data: { ...dialog.data, asset_tag: e.target.value } })}
                      placeholder="e.g. INS-001"
                      data-testid="inv-asset-tag"
                    />
                  </div>
                  <div>
                    <Label>Instrument Type *</Label>
                    <Input
                      value={dialog.data?.subcategory || ""}
                      onChange={(e) => setDialog({ open: true, data: { ...dialog.data, subcategory: e.target.value } })}
                      placeholder="e.g. Bb Cornet, Trombone"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Make</Label>
                    <Input
                      value={dialog.data?.make || ""}
                      onChange={(e) => setDialog({ open: true, data: { ...dialog.data, make: e.target.value } })}
                      placeholder="e.g. Yamaha, Besson"
                    />
                  </div>
                  <div>
                    <Label>Finish</Label>
                    <Input
                      value={dialog.data?.finish || ""}
                      onChange={(e) => setDialog({ open: true, data: { ...dialog.data, finish: e.target.value } })}
                      placeholder="e.g. Silver-plated, Lacquer"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Serial Number</Label>
                    <Input
                      value={dialog.data?.serial_number || ""}
                      onChange={(e) => setDialog({ open: true, data: { ...dialog.data, serial_number: e.target.value } })}
                    />
                  </div>
                  <div>
                    <Label>Purchase Date</Label>
                    <Input
                      type="date"
                      value={dialog.data?.purchase_date || ""}
                      onChange={(e) => setDialog({ open: true, data: { ...dialog.data, purchase_date: e.target.value } })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Vendor</Label>
                    <Input
                      value={dialog.data?.supplier || ""}
                      onChange={(e) => setDialog({ open: true, data: { ...dialog.data, supplier: e.target.value } })}
                      placeholder="Where it was purchased"
                    />
                  </div>
                  <div>
                    <Label>Value (£)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={dialog.data?.purchase_price ?? ""}
                      onChange={(e) => setDialog({ open: true, data: { ...dialog.data, purchase_price: e.target.value } })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Condition</Label>
                  <Select value={dialog.data?.condition || "good"} onValueChange={(v) => setDialog({ open: true, data: { ...dialog.data, condition: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c} className="capitalize">{formatCondition(c)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* ===== ACCESSORY FORM ===== */}
            {dialog.data?.category === "accessory" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Asset Tag</Label>
                    <Input
                      value={dialog.data?.asset_tag || ""}
                      onChange={(e) => setDialog({ open: true, data: { ...dialog.data, asset_tag: e.target.value } })}
                      placeholder="e.g. ACC-001"
                      data-testid="inv-asset-tag"
                    />
                  </div>
                  <div>
                    <Label>Make</Label>
                    <Input
                      value={dialog.data?.make || ""}
                      onChange={(e) => setDialog({ open: true, data: { ...dialog.data, make: e.target.value } })}
                      placeholder="e.g. Manhasset"
                    />
                  </div>
                </div>
                <div>
                  <Label>Item Name *</Label>
                  <Input
                    value={dialog.data?.name || ""}
                    onChange={(e) => setDialog({ open: true, data: { ...dialog.data, name: e.target.value } })}
                    placeholder="e.g. Music stand, Folder"
                    data-testid="inv-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Purchase Date</Label>
                    <Input
                      type="date"
                      value={dialog.data?.purchase_date || ""}
                      onChange={(e) => setDialog({ open: true, data: { ...dialog.data, purchase_date: e.target.value } })}
                    />
                  </div>
                  <div>
                    <Label>Value (£)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={dialog.data?.purchase_price ?? ""}
                      onChange={(e) => setDialog({ open: true, data: { ...dialog.data, purchase_price: e.target.value } })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Condition</Label>
                  <Select value={dialog.data?.condition || "good"} onValueChange={(v) => setDialog({ open: true, data: { ...dialog.data, condition: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c} className="capitalize">{formatCondition(c)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* ===== UNIFORM FORM ===== */}
            {dialog.data?.category === "uniform" && (
              <>
                <div>
                  <Label>Uniform Type *</Label>
                  {uniformOptions.length > 0 ? (
                    <Select
                      value={dialog.data?.subcategory || ""}
                      onValueChange={(v) => setDialog({ open: true, data: { ...dialog.data, subcategory: v } })}
                    >
                      <SelectTrigger data-testid="inv-uniform-type"><SelectValue placeholder="Pick a uniform option" /></SelectTrigger>
                      <SelectContent>{uniformOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <div className="text-xs rounded-md border border-amber-500/30 bg-amber-500/10 p-2">
                      Your band doesn't have any uniform options yet.{" "}
                      <button
                        className="underline text-amber-300"
                        onClick={() => navigate("/settings?tab=band")}
                      >
                        Set them in Settings
                      </button>{" "}
                      first.
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Size</Label>
                    <Input
                      value={dialog.data?.size || ""}
                      onChange={(e) => setDialog({ open: true, data: { ...dialog.data, size: e.target.value } })}
                      placeholder="e.g. M, 38R"
                    />
                  </div>
                  <div>
                    <Label>Condition</Label>
                    <Select value={dialog.data?.condition || "good"} onValueChange={(v) => setDialog({ open: true, data: { ...dialog.data, condition: v } })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c} className="capitalize">{formatCondition(c)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Purchase Date</Label>
                    <Input
                      type="date"
                      value={dialog.data?.purchase_date || ""}
                      onChange={(e) => setDialog({ open: true, data: { ...dialog.data, purchase_date: e.target.value } })}
                    />
                  </div>
                  <div>
                    <Label>Vendor</Label>
                    <Input
                      value={dialog.data?.supplier || ""}
                      onChange={(e) => setDialog({ open: true, data: { ...dialog.data, supplier: e.target.value } })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Value (£)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={dialog.data?.purchase_price ?? ""}
                    onChange={(e) => setDialog({ open: true, data: { ...dialog.data, purchase_price: e.target.value } })}
                  />
                </div>
              </>
            )}

            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={dialog.data?.notes || ""} onChange={(e) => setDialog({ open: true, data: { ...dialog.data, notes: e.target.value } })} />
            </div>

            {/* Photo — only visible when editing an existing item (needs an id) */}
            {dialog.data?.id && (
              <div>
                <Label>Photo</Label>
                <div className="flex items-center gap-3 mt-1.5">
                  {dialog.data.photo_path ? (
                    <button
                      type="button"
                      onClick={() => setPhotoPreview({ id: dialog.data.id, name: dialog.data.name })}
                      className="w-20 h-20 rounded-md overflow-hidden border border-white/10 hover:border-primary/50 transition"
                      data-testid="inv-dialog-photo-thumb"
                    >
                      <img src={`${API}/inventory/${dialog.data.id}/photo?t=${Date.now()}`} alt="" className="w-full h-full object-cover" />
                    </button>
                  ) : (
                    <div className="w-20 h-20 rounded-md border border-dashed border-white/10 flex items-center justify-center text-muted-foreground/60">
                      <ImageIcon className="w-6 h-6" />
                    </div>
                  )}
                  <div className="flex-1 space-y-1">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                      id="inv-photo-file"
                      className="hidden"
                      data-testid="inv-photo-file"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        if (!file) return;
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error("Photo must be 5 MB or smaller");
                          return;
                        }
                        setPhotoUploading(true);
                        try {
                          const form = new FormData();
                          form.append("file", file);
                          await apiClient.post(`/inventory/${dialog.data.id}/photo`, form, {
                            headers: { "Content-Type": "multipart/form-data" },
                          });
                          toast.success("Photo uploaded");
                          setDialog({ open: true, data: { ...dialog.data, photo_path: "new" } });
                          loadAll();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Upload failed");
                        } finally {
                          setPhotoUploading(false);
                        }
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => document.getElementById("inv-photo-file").click()}
                      disabled={photoUploading}
                      data-testid="inv-photo-upload-btn"
                    >
                      <Upload className="w-3.5 h-3.5 mr-2" />
                      {photoUploading ? "Uploading..." : dialog.data.photo_path ? "Replace photo" : "Upload photo"}
                    </Button>
                    {dialog.data.photo_path && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:text-red-300"
                        onClick={async () => {
                          if (!confirm("Remove photo from this item?")) return;
                          try {
                            await apiClient.delete(`/inventory/${dialog.data.id}/photo`);
                            toast.success("Photo removed");
                            setDialog({ open: true, data: { ...dialog.data, photo_path: null } });
                            loadAll();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Failed to remove");
                          }
                        }}
                        data-testid="inv-photo-remove-btn"
                      >
                        <XIcon className="w-3.5 h-3.5 mr-1" />
                        Remove
                      </Button>
                    )}
                    <p className="text-[11px] text-muted-foreground">PNG / JPG / WebP / GIF — up to 5 MB</p>
                  </div>
                </div>
              </div>
            )}
            {!dialog.data?.id && (
              <p className="text-xs text-muted-foreground italic">Save the item first, then edit it again to attach a photo.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, data: null })}>Cancel</Button>
            <Button onClick={handleSave} data-testid="inv-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue dialog */}
      <Dialog open={issueDialog.open} onOpenChange={(o) => !o && setIssueDialog({ ...issueDialog, open: false })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Issue “{issueDialog.item?.name}”</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Player *</Label>
              <PlayerPicker
                value={issueDialog.player_id}
                players={players}
                sections={sections}
                onChange={(v) => setIssueDialog({ ...issueDialog, player_id: v || "" })}
                placeholder="Select player"
                testId="issue-player"
              />
            </div>
            <div>
              <Label>Return Due Date</Label>
              <Input type="date" value={issueDialog.return_due_date} onChange={(e) => setIssueDialog({ ...issueDialog, return_due_date: e.target.value })} />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={issueDialog.notes} onChange={(e) => setIssueDialog({ ...issueDialog, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIssueDialog({ ...issueDialog, open: false })}>Cancel</Button>
            <Button onClick={handleIssue} disabled={!issueDialog.player_id} data-testid="issue-confirm">Issue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return dialog */}
      <Dialog open={returnDialog.open} onOpenChange={(o) => !o && setReturnDialog({ ...returnDialog, open: false })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Return “{returnDialog.item?.name}”</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Returned Condition</Label>
              <Select value={returnDialog.returned_condition} onValueChange={(v) => setReturnDialog({ ...returnDialog, returned_condition: v })}>
                <SelectTrigger><SelectValue placeholder="Unchanged" /></SelectTrigger>
                <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={returnDialog.notes} onChange={(e) => setReturnDialog({ ...returnDialog, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnDialog({ ...returnDialog, open: false })}>Cancel</Button>
            <Button onClick={handleReturn} data-testid="return-confirm">Return to Store Room</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={historyDialog.open} onOpenChange={(o) => !o && setHistoryDialog({ open: false, item: null, loans: [] })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Loan History — {historyDialog.item?.name}</DialogTitle></DialogHeader>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Player</TableHead><TableHead>Issued</TableHead>
              <TableHead>Returned</TableHead><TableHead>Condition</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {historyDialog.loans.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-4">No loan history.</TableCell></TableRow>
              )}
              {historyDialog.loans.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{l.player_name}</TableCell>
                  <TableCell className="text-xs">{l.issued_date?.slice(0, 10)}</TableCell>
                  <TableCell className="text-xs">{l.returned_date ? l.returned_date.slice(0, 10) : <Badge variant="outline">Open</Badge>}</TableCell>
                  <TableCell className="text-xs">{l.returned_condition || l.issued_condition || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      {/* Photo preview lightbox */}
      <Dialog open={!!photoPreview} onOpenChange={(o) => !o && setPhotoPreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="inv-photo-preview-dialog">
          <DialogHeader>
            <DialogTitle>{photoPreview?.name || "Item photo"}</DialogTitle>
          </DialogHeader>
          {photoPreview && (
            <div className="rounded-md overflow-hidden bg-black/40 flex items-center justify-center">
              <img
                src={`${API}/inventory/${photoPreview.id}/photo?t=${Date.now()}`}
                alt={photoPreview.name}
                className="max-h-[70vh] max-w-full object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inventory;
